/**
 * Admin AI configuration panel. Three provider cards (Claude / Gemini / OpenAI),
 * each with a masked API-key field + Save, a Test button, and a Model selector
 * populated from the Worker's cached `listModels`. An active-provider selector
 * chooses the answering brain.
 *
 * RENDER GATE: this component must only be mounted for admin users — the caller
 * gates it (see `AdminPanel`), but the Worker independently enforces the admin
 * claim on every request. Non-admins never reach the data.
 *
 * The `deps` (Worker URL + fresh-token provider) are injected so tests drive the
 * panel with a fake fetch and no Firebase/network.
 */

import { useState } from "react";
import type { ProviderId } from "@autodieta/shared";
import {
  useAIConfig,
  useAIModels,
  useTestProvider,
  type AdminHooksDeps,
} from "./hooks.js";

const PROVIDERS: ReadonlyArray<{ id: ProviderId; label: string }> = [
  { id: "claude", label: "Claude (Anthropic)" },
  { id: "gemini", label: "Gemini (Google)" },
  { id: "openai", label: "OpenAI" },
];

export interface AIPanelProps {
  deps: AdminHooksDeps;
}

export function AIPanel({ deps }: AIPanelProps): JSX.Element {
  const cfg = useAIConfig(deps);
  const tester = useTestProvider(deps);

  if (cfg.loading && !cfg.config) {
    return (
      <p role="status" aria-live="polite" className="flex items-center gap-3 text-sm text-zinc-400">
        <span
          aria-hidden="true"
          className="h-4 w-4 animate-spin rounded-full border-2 border-zinc-700 border-t-blue-400"
        />
        Cargando configuración de IA…
      </p>
    );
  }

  if (cfg.error) {
    return (
      <p role="alert" className="text-sm text-red-400">
        {cfg.error}
      </p>
    );
  }

  return (
    <section aria-label="ai-configuration" className="space-y-5">
      <h2 className="text-base font-semibold text-zinc-100">Proveedores IA</h2>

      <label className="flex max-w-xs flex-col gap-1">
        <span className="text-xs font-medium text-zinc-400">Proveedor activo</span>
        <select
          aria-label="active-provider"
          className="rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-zinc-100"
          value={cfg.config?.activeProvider ?? "claude"}
          onChange={(e) => void cfg.setActive(e.target.value as ProviderId)}
        >
          {PROVIDERS.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}
            </option>
          ))}
        </select>
      </label>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {PROVIDERS.map((p) => (
          <ProviderCard
            key={p.id}
            deps={deps}
            providerId={p.id}
            label={p.label}
            configured={cfg.config?.providers[p.id]?.configured ?? false}
            masked={cfg.config?.providers[p.id]?.masked ?? ""}
            model={cfg.config?.providers[p.id]?.model ?? ""}
            onSaveKey={(key) => cfg.setKey(p.id, key)}
            onSaveModel={(model) => cfg.setModel(p.id, model)}
            onTest={() => tester.test(p.id)}
            testResult={tester.result}
            testing={tester.testing}
          />
        ))}
      </div>
    </section>
  );
}

interface ProviderCardProps {
  deps: AdminHooksDeps;
  providerId: ProviderId;
  label: string;
  configured: boolean;
  masked: string;
  model: string;
  onSaveKey: (key: string) => Promise<{ ok: boolean; masked: string }>;
  onSaveModel: (model: string) => Promise<void>;
  onTest: () => Promise<void>;
  testResult: { ok: boolean; message: string } | null;
  testing: boolean;
}

function ProviderCard({
  deps,
  providerId,
  label,
  configured,
  masked,
  model,
  onSaveKey,
  onSaveModel,
  onTest,
  testResult,
  testing,
}: ProviderCardProps): JSX.Element {
  const [keyInput, setKeyInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [keyError, setKeyError] = useState<string | null>(null);
  const modelsHook = useAIModels(deps, providerId);

  const handleSaveKey = async () => {
    setSaving(true);
    setKeyError(null);
    try {
      await onSaveKey(keyInput);
      setKeyInput("");
    } catch {
      setKeyError("No se pudo guardar la clave.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <article
      aria-label={`provider-${providerId}`}
      className="flex flex-col gap-3 rounded-xl border border-zinc-800 bg-zinc-950/40 p-4"
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-base font-semibold text-zinc-100">{label}</h3>
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
            configured
              ? "bg-blue-400/10 text-blue-400"
              : "text-zinc-500"
          }`}
        >
          {configured ? "Configurada" : "Sin configurar"}
        </span>
      </div>
      <p
        data-testid={`status-${providerId}`}
        className="truncate text-xs text-zinc-500"
      >
        {configured ? `Configurada: ${masked}` : "Sin configurar"}
      </p>

      <label className="flex flex-col gap-1">
        <span className="text-xs font-medium text-zinc-400">API KEY</span>
        <input
          type="password"
          aria-label={`api-key-${providerId}`}
          className="rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-zinc-100"
          value={keyInput}
          placeholder={configured ? masked : "Ingresá la API KEY"}
          onChange={(e) => setKeyInput(e.target.value)}
        />
      </label>
      <div className="flex items-center gap-2">
        <button
          type="button"
          aria-label={`save-key-${providerId}`}
          className="min-h-[44px] rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-blue-500 disabled:opacity-50"
          disabled={saving || keyInput.length === 0}
          onClick={() => void handleSaveKey()}
        >
          Guardar
        </button>
        <button
          type="button"
          aria-label={`test-${providerId}`}
          className="min-h-[44px] rounded-lg border border-zinc-700 px-3 py-1.5 text-xs font-medium text-zinc-300 transition-colors hover:bg-zinc-800 disabled:opacity-50"
          disabled={testing}
          onClick={() => void onTest()}
        >
          Probar
        </button>
      </div>
      {keyError ? (
        <span role="alert" className="text-xs text-red-400">
          {keyError}
        </span>
      ) : null}
      {testResult ? (
        <span
          data-testid={`test-result-${providerId}`}
          className={`text-xs ${testResult.ok ? "text-blue-400" : "text-red-400"}`}
        >
          {testResult.message}
        </span>
      ) : null}

      <div className="mt-1 space-y-2 border-t border-zinc-800 pt-3">
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-zinc-400">MODELO</span>
          <select
            aria-label={`model-${providerId}`}
            className="rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-zinc-100"
            value={model}
            onChange={(e) => void onSaveModel(e.target.value)}
          >
            <option value={model}>{model || "(ninguno)"}</option>
            {modelsHook.models
              .filter((m) => m.id !== model)
              .map((m) => (
                <option key={m.id} value={m.id}>
                  {m.display_name}
                </option>
              ))}
          </select>
        </label>
        <button
          type="button"
          aria-label={`load-models-${providerId}`}
          className="min-h-[44px] rounded-lg border border-zinc-700 px-3 py-1.5 text-xs font-medium text-zinc-300 transition-colors hover:bg-zinc-800 disabled:opacity-50"
          disabled={!configured || modelsHook.loading}
          onClick={() => void modelsHook.load()}
        >
          Cargar modelos
        </button>
        {modelsHook.error ? (
          <span role="alert" className="block text-xs text-red-400">
            {modelsHook.error}
          </span>
        ) : null}
      </div>
    </article>
  );
}
